/*
 * Copyright 2026 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  type BackstageTypography,
  type PageTheme,
  type UnifiedThemeOptions,
} from '@backstage/theme';

export const EMBER_THEME_NAME = 'ember';

const fontFamily =
  '"Inter", "Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

const brand = {
  ember: '#AA3322',
  emberLight: '#E5664F',
  emberLighter: '#F08A75',
  emberDark: '#7E2418',
};

const pageTheme: Record<string, PageTheme> = {
  home: genPageTheme({ colors: ['#5E1A10', '#AA3322'], shape: shapes.wave }),
  service: genPageTheme({ colors: ['#2B2622', '#7A2C1F'], shape: shapes.wave }),
  website: genPageTheme({ colors: ['#1F2B33', '#3D5A6B'], shape: shapes.wave }),
  library: genPageTheme({ colors: ['#4F3010', '#9C6526'], shape: shapes.wave }),
  documentation: genPageTheme({
    colors: ['#33271F', '#80553A'],
    shape: shapes.wave2,
  }),
  tool: genPageTheme({ colors: ['#3D1824', '#93344F'], shape: shapes.round }),
  app: genPageTheme({ colors: ['#7E2418', '#C2462E'], shape: shapes.wave }),
  apis: genPageTheme({ colors: ['#1C332D', '#3E6A5D'], shape: shapes.wave2 }),
  card: genPageTheme({ colors: ['#2B2622', '#5A4A3F'], shape: shapes.wave }),
  other: genPageTheme({ colors: ['#1E1C1A', '#3B3733'], shape: shapes.wave }),
};

const typography: BackstageTypography = {
  htmlFontSize: 16,
  fontFamily,
  h1: { fontSize: 40, fontWeight: 700, marginBottom: 10 },
  h2: { fontSize: 32, fontWeight: 700, marginBottom: 8 },
  h3: { fontSize: 26, fontWeight: 600, marginBottom: 6 },
  h4: { fontSize: 22, fontWeight: 600, marginBottom: 6 },
  h5: { fontSize: 19, fontWeight: 600, marginBottom: 4 },
  h6: { fontSize: 16, fontWeight: 600, marginBottom: 2 },
};

const darkSurface = {
  app: '#121110',
  paper: '#1A1918',
  raised: '#211F1D',
  hover: '#2A2825',
  border: '#2E2B28',
  text: '#ECE9E2',
  textSubtle: '#A8A49B',
  textVerySubtle: '#6F6B64',
};

const lightSurface = {
  app: '#F6F4F1',
  paper: '#FFFFFF',
  raised: '#FBFAF8',
  hover: '#F1EEEA',
  border: '#E4DFD8',
  text: '#24211E',
  textSubtle: '#5C574F',
  textVerySubtle: '#8F8A82',
};

type Surface = typeof darkSurface;

function components(surface: Surface): UnifiedThemeOptions['components'] {
  return {
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 10 },
        outlined: { borderColor: surface.border },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${surface.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 8, fontWeight: 600 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${surface.border}` },
        head: {
          color: surface.textSubtle,
          fontWeight: 600,
          textTransform: 'none',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: { textUnderlineOffset: 3 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: surface.raised,
          color: surface.text,
          border: `1px solid ${surface.border}`,
          fontSize: 12,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: {
          boxShadow: 'none',
          borderBottom: `1px solid ${surface.border}`,
        },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          borderRight: `1px solid ${surface.border}`,
        },
      },
    },
  };
}

export const emberDarkTheme = createUnifiedTheme({
  fontFamily,
  typography,
  pageTheme,
  defaultPageTheme: 'home',
  palette: {
    ...palettes.dark,
    background: { default: darkSurface.app, paper: darkSurface.paper },
    primary: {
      main: brand.emberLight,
      light: brand.emberLighter,
      dark: brand.ember,
      contrastText: '#1A0B08',
    },
    secondary: { main: '#D9A066', contrastText: '#1A1208' },
    text: {
      primary: darkSurface.text,
      secondary: darkSurface.textSubtle,
      disabled: darkSurface.textVerySubtle,
    },
    divider: darkSurface.border,
    border: darkSurface.border,
    textContrast: darkSurface.text,
    textSubtle: darkSurface.textSubtle,
    textVerySubtle: darkSurface.textVerySubtle,
    link: brand.emberLighter,
    linkHover: '#F7AE9E',
    highlight: '#3A2A12',
    status: {
      ok: '#5FC48A',
      warning: '#E8A94A',
      error: '#F0614F',
      running: '#6FA8DC',
      pending: '#E3C75A',
      aborted: '#8A857D',
    },
    banner: {
      ...palettes.dark.banner,
      info: '#2F4B63',
      error: brand.emberDark,
      warning: '#8A5A12',
      link: '#FFFFFF',
    },
    navigation: {
      background: '#161514',
      indicator: brand.emberLight,
      color: darkSurface.textSubtle,
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: darkSurface.hover },
      submenu: { background: darkSurface.raised },
    },
    pinSidebarButton: {
      icon: darkSurface.paper,
      background: darkSurface.textSubtle,
    },
    tabbar: { indicator: brand.emberLight },
  },
  components: components(darkSurface),
});

export const emberLightTheme = createUnifiedTheme({
  fontFamily,
  typography,
  pageTheme,
  defaultPageTheme: 'home',
  palette: {
    ...palettes.light,
    background: { default: lightSurface.app, paper: lightSurface.paper },
    primary: {
      main: brand.ember,
      light: brand.emberLight,
      dark: brand.emberDark,
      contrastText: '#FFFFFF',
    },
    secondary: { main: '#9C6526', contrastText: '#FFFFFF' },
    text: {
      primary: lightSurface.text,
      secondary: lightSurface.textSubtle,
      disabled: lightSurface.textVerySubtle,
    },
    divider: lightSurface.border,
    border: lightSurface.border,
    textSubtle: lightSurface.textSubtle,
    textVerySubtle: lightSurface.textVerySubtle,
    link: brand.ember,
    linkHover: brand.emberDark,
    navigation: {
      background: '#1A1918',
      indicator: brand.emberLight,
      color: '#B3AEA5',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: '#2A2825' },
      submenu: { background: '#211F1D' },
    },
    tabbar: { indicator: brand.ember },
  },
  components: components(lightSurface),
});
